import { QueryClient, type Query } from 'react-query';
import { ApiError } from './api.js';

/** The localStorage key the offline copy of the query cache is persisted under. */
export const QUERY_CACHE_STORAGE_KEY = 'buildc-query-cache';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // A 4xx is the server's settled answer — retrying a 404 only holds a record page on
        // "Loading…" before it says the record is gone. 408 and 429 are the exceptions.
        if (
          error instanceof ApiError &&
          error.statusCode >= 400 && error.statusCode < 500 &&
          ![408, 429].includes(error.statusCode)
        ) {
          return false;
        }
        return failureCount < 2;
      },
      staleTime: 30_000,
      // Keep successful queries in cache for 24h so they survive page reload offline
      cacheTime: 24 * 60 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Alert queries are never written to localStorage. What an alert list holds depends on the
 * role (margin and cost rules are hidden from builder_staff), and the keys carry no user or
 * role, so a persisted copy would show the next person on the device what their role may
 * not see. They are cheap to fetch again and have no use offline.
 */
const UNPERSISTED_ROOTS = new Set(['alerts', 'alert', 'alerts-summary', 'alert-rules', 'admin-email-health']);

export function shouldPersistQuery(query: Pick<Query, 'queryKey' | 'state'>): boolean {
  if (query.state.status !== 'success') return false;
  const root = Array.isArray(query.queryKey) ? query.queryKey[0] : query.queryKey;
  return !(typeof root === 'string' && UNPERSISTED_ROOTS.has(root));
}

/** Drops every cached query, in memory and the persisted copy, so nothing outlives a sign-out. */
export function clearQueryCache(client: QueryClient = queryClient): void {
  client.clear();
  try {
    window.localStorage.removeItem(QUERY_CACHE_STORAGE_KEY);
  } catch {
    // Storage can throw in a private window; there is then nothing persisted to remove.
  }
}
